import { useMemo } from 'react'
import { createPublicClient, http } from 'viem'
import { arbitrum, bsc } from 'viem/chains'
import { useErc20BalanceOfWatcher } from '@/components/Deposit/src/hooks/useErc20BalanceOfWatcher'

// Public, well-known token addresses (mainnet)
const BSC_USDT = '0x55d398326f99059fF775485246999027B3197955' as const
const ARB_USDT = '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9' as const
const ARB_USDC = '0xaf88d065e77c8cC2239327C5EDb3A432268e5831' as const

// Pool (holder) contract addresses provided by user
const BSC_POOL = '0x0520b0a951658db92b8a2dd9f146bb8223638740' as const
const ARB_POOL_USDT = '0xceeed84620e5eb9ab1d6dfc316867d2cda332e41' as const
const ARB_POOL_USDC = '0x81144d6E7084928830f9694a201E8c1ce6eD0cb2' as const

export interface UsePoolBalanceParams {
  bscRpcUrl?: string
  arbitrumRpcUrl?: string
  pollingInterval?: number
}

function pickRpc(
  preferred: string | undefined,
  fallbackA?: string,
  fallbackList: readonly string[] = []
) {
  if (preferred && typeof preferred === 'string') return preferred
  if (fallbackA && typeof fallbackA === 'string') return fallbackA
  if (fallbackList && fallbackList.length > 0) return fallbackList[0]
  return undefined
}

/**
 * usePoolBalance
 * Reads pool balances for:
 * - BNB_USDT (USDT on BSC, holder = BSC_POOL)
 * - ARB_USDT (USDT on Arbitrum, holder = ARB_POOL_USDT)
 * - ARB_USDC (USDC on Arbitrum, holder = ARB_POOL_USDC)
 * Returns formatted strings; defaults to '0' if not available yet.
 * In testnet environment, returns '99999' instead of real balances.
 */
export function usePoolBalance(params: UsePoolBalanceParams = {}) {
  const { bscRpcUrl, arbitrumRpcUrl, pollingInterval = 12_000 } = params

  // Detect testnet environment based on RPC URLs
  const isTestnet = useMemo(() => {
    const testnetPattern = /testnet|sepolia/i
    return (
      testnetPattern.test(bscRpcUrl || '') ||
      testnetPattern.test(arbitrumRpcUrl || '')
    )
  }, [bscRpcUrl, arbitrumRpcUrl])

  // viem clients
  const bscClient = useMemo(
    () =>
      createPublicClient({
        chain: bsc,
        transport: http(
          pickRpc(
            bscRpcUrl,
            typeof window !== 'undefined'
              ? window?.ENV?.['CHAIN_RPC_URL_56']
              : undefined,
            bsc.rpcUrls?.default?.http || []
          )
        ),
      }),
    [bscRpcUrl]
  )

  const arbClient = useMemo(
    () =>
      createPublicClient({
        chain: arbitrum,
        transport: http(
          pickRpc(
            arbitrumRpcUrl,
            typeof window !== 'undefined'
              ? window?.ENV?.['CHAIN_RPC_URL_42161']
              : undefined,
            arbitrum.rpcUrls?.default?.http || []
          )
        ),
      }),
    [arbitrumRpcUrl]
  )

  // Watchers (defaults: decimals 6 for USDT/USDC)
  const { formatted: BNB_USDT_BALANCE = '0' } = useErc20BalanceOfWatcher({
    client: bscClient,
    tokenAddress: BSC_USDT,
    holder: BSC_POOL,
    decimals: 18, // BSC USDT uses 18 decimals
    fallbackFormatted: '0',
    pollingInterval,
  })

  const { formatted: ARB_USDT_BALANCE = '0' } = useErc20BalanceOfWatcher({
    client: arbClient,
    tokenAddress: ARB_USDT,
    holder: ARB_POOL_USDT,
    decimals: 6,
    fallbackFormatted: '0',
    pollingInterval,
  })

  const { formatted: ARB_USDC_BALANCE = '0' } = useErc20BalanceOfWatcher({
    client: arbClient,
    tokenAddress: ARB_USDC,
    holder: ARB_POOL_USDC,
    decimals: 6,
    fallbackFormatted: '0',
    pollingInterval,
  })

  // Return mock balances for testnet environment
  if (isTestnet) {
    return {
      BNB_USDT_BALANCE: '99999',
      ARB_USDT_BALANCE: '99999',
      ARB_USDC_BALANCE: '99999',
    }
  }

  return {
    BNB_USDT_BALANCE,
    ARB_USDT_BALANCE,
    ARB_USDC_BALANCE,
  }
}
